import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Backdrop, Box, Typography, IconButton } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SehoLoadingSpinner from "./SehoLoadingSpinner";
import { getLangFromCookie } from "../../Utils/LangCookie";

/**
 * Lightweight global controller (no React inside).
 * http.js & API modules can call LoadingController.start/stop/reset
 * without using hooks.
 */
let subscribers = new Set();
let pendingCount = 0;

let cancelEnabled = false; // default: OFF
let onCancelCallback = null; // optional hook for abort logic

export const LoadingController = {
  start() {
    pendingCount += 1;
    notify();
  },
  stop() {
    if (pendingCount > 0) pendingCount -= 1;
    notify();
  },
  reset() {
    pendingCount = 0;
    notify();
  },

  /** Turn cancel UI on/off */
  setCancelable(enabled) {
    cancelEnabled = !!enabled;
    notify();
  },

  /** Optional: what to do when user cancels */
  setOnCancel(fn) {
    onCancelCallback = typeof fn === "function" ? fn : null;
  },

  /** Called by the overlay when user clicks X */
  cancel() {
    // 1) Immediately hide overlay
    pendingCount = 0;

    // 2) Let your app abort requests if you wired it (optional)
    try {
      if (onCancelCallback) onCancelCallback();
    } catch (e) {
      // ignore
    }

    notify();
  },
};

function notify() {
  for (const fn of subscribers) {
    try {
      fn(pendingCount, cancelEnabled);
    } catch (e) {
      // ignore subscriber errors
    }
  }
}

/* ------------ React side: context + overlay UI ------------ */

const LoadingContext = createContext({
  isLoading: false,
  startLoading: () => {},
  stopLoading: () => {},
  resetLoading: () => {},
  setCancelable: () => {},
  setOnCancel: () => {},
  cancelLoading: () => {},
});

export function useLoading() {
  return useContext(LoadingContext);
}

const normalizeLoadingLang = (code) => {
  const value = String(code || "")
    .trim()
    .toUpperCase();

  if (value === "ZH" || value === "JP") {
    return value;
  }

  return "EN";
};

const LOADING_TEXT = {
  ZH: {
    title: "讀取中，請稍候…",
    preparing: "正在準備頁面與圖片",
    cancelHint: "可按 ✕ 取消讀取",
    cancelAria: "取消讀取",
  },
  EN: {
    title: "Loading, please wait…",
    preparing: "Preparing the page and images",
    cancelHint: "Press ✕ to cancel loading",
    cancelAria: "Cancel loading",
  },
  JP: {
    title: "読み込み中です。しばらくお待ちください…",
    preparing: "ページと画像を準備しています",
    cancelHint: "✕を押すと読み込みをキャンセルできます",
    cancelAria: "読み込みをキャンセル",
  },
};

/**
 * Wrap your app with this ONCE (e.g. in App.jsx)
 * It listens to LoadingController and renders the overlay.
 *
 * Props:
 * - cancelable?: boolean (default false) -> enables the X button UI
 * - onCancel?: () => void -> optional abort handler
 */
export function LoadingOverlay({ children, cancelable = false, onCancel }) {
  const [count, setCount] = useState(0);
  const [canCancel, setCanCancel] = useState(false);
  const [lang, setLang] = useState(() =>
    normalizeLoadingLang(getLangFromCookie()),
  );

  const pageContentRef = useRef(null);
  const pausedMediaRef = useRef(new Set());

  const text = LOADING_TEXT[lang] || LOADING_TEXT.EN;

  const brandTeal = "#0D9EAF";
  const brandBrown = "#D69A3A";
  const brandText = "#4E5A61";

  // keep controller in sync with props
  useEffect(() => {
    const updateLanguage = () => {
      setLang(normalizeLoadingLang(getLangFromCookie()));
    };

    updateLanguage();

    window.addEventListener("app:languageChanged", updateLanguage);

    return () => {
      window.removeEventListener("app:languageChanged", updateLanguage);
    };
  }, []);

  // keep controller in sync with props
  useEffect(() => {
    LoadingController.setCancelable(cancelable);
  }, [cancelable]);

  useEffect(() => {
    LoadingController.setOnCancel(onCancel);
  }, [onCancel]);

  useEffect(() => {
    const update = (c, enabled) => {
      setCount(c);
      setCanCancel(!!enabled);
    };

    subscribers.add(update);

    // init once
    update(pendingCount, cancelEnabled);

    return () => {
      subscribers.delete(update);
    };
  }, []);

  const open = count > 0;

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("seho:loadingChanged", {
        detail: {
          isLoading: open,
        },
      }),
    );
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyTouchAction = document.body.style.touchAction;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";

    return () => {
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.touchAction = previousBodyTouchAction;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !pageContentRef.current) {
      return undefined;
    }

    const pageContent = pageContentRef.current;
    const pausedMedia = pausedMediaRef.current;

    const pauseMedia = (mediaElement) => {
      if (
        !(mediaElement instanceof HTMLMediaElement) ||
        mediaElement.paused ||
        mediaElement.ended
      ) {
        return;
      }

      pausedMedia.add(mediaElement);
      mediaElement.pause();
    };

    pageContent.querySelectorAll("video, audio").forEach(pauseMedia);

    const handleMediaPlay = (event) => {
      pauseMedia(event.target);
    };

    pageContent.addEventListener("play", handleMediaPlay, true);

    const mediaObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) {
            return;
          }

          if (node.matches("video, audio")) {
            pauseMedia(node);
          }

          node.querySelectorAll?.("video, audio").forEach(pauseMedia);
        });
      });
    });

    mediaObserver.observe(pageContent, {
      childList: true,
      subtree: true,
    });

    return () => {
      pageContent.removeEventListener("play", handleMediaPlay, true);

      mediaObserver.disconnect();

      const mediaToResume = Array.from(pausedMedia);
      pausedMedia.clear();

      mediaToResume.forEach((mediaElement) => {
        if (!mediaElement.isConnected || mediaElement.ended) {
          return;
        }

        const playResult = mediaElement.play();

        if (playResult?.catch) {
          playResult.catch(() => {});
        }
      });
    };
  }, [open]);

  const value = {
    isLoading: open,
    startLoading: LoadingController.start,
    stopLoading: LoadingController.stop,
    resetLoading: LoadingController.reset,
    setCancelable: LoadingController.setCancelable,
    setOnCancel: LoadingController.setOnCancel,
    cancelLoading: LoadingController.cancel,
  };

  return (
    <LoadingContext.Provider value={value}>
      <Box
        ref={pageContentRef}
        aria-hidden={open ? "true" : undefined}
        inert={open ? "" : undefined}
        sx={{
          pointerEvents: open ? "none" : "auto",
          userSelect: open ? "none" : "auto",
          "& *, & *::before, & *::after": {
            animationPlayState: open ? "paused !important" : undefined,
            transition: open ? "none !important" : undefined,
          },
        }}
      >
        {children}
      </Box>

      <Backdrop
        open={open}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 200,
          backgroundColor: "#f7fbfc",
          backgroundImage: "linear-gradient(135deg, #f7fbfc 0%, #edf8f9 100%)",
          backdropFilter: "none",
          WebkitBackdropFilter: "none",
          pointerEvents: "auto",
          touchAction: "none",
        }}
      >
        <Box
          sx={{
            "@keyframes sehoSpinClockwise": {
              "0%": {
                transform: "rotate(0deg)",
              },
              "100%": {
                transform: "rotate(360deg)",
              },
            },
            "@keyframes sehoSpinCounter": {
              "0%": {
                transform: "rotate(360deg)",
              },
              "100%": {
                transform: "rotate(0deg)",
              },
            },
            "@keyframes sehoPulse": {
              "0%": {
                transform: "scale(0.92)",
                opacity: 0.75,
              },
              "50%": {
                transform: "scale(1)",
                opacity: 1,
              },
              "100%": {
                transform: "scale(0.92)",
                opacity: 0.75,
              },
            },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2.25,
            px: { xs: 3, sm: 4 },
            py: { xs: 3.2, sm: 3.6 },
            borderRadius: 4,
            minWidth: { xs: 240, sm: 280 },
            maxWidth: "90vw",
            position: "relative",
            bgcolor: "rgba(255,255,255,0.74)",
            border: "1px solid rgba(13,158,175,0.18)",
            boxShadow:
              "0 18px 48px rgba(13,158,175,0.12), 0 8px 18px rgba(0,0,0,0.06)",
            backdropFilter: "blur(14px)",
          }}
        >
          {/* animated brand spinner */}
          <SehoLoadingSpinner
            variant="overlay"
            centerContent={
              canCancel ? (
                <IconButton
                  onClick={() => LoadingController.cancel()}
                  aria-label={text.cancelAria}
                  sx={{
                    width: {
                      xs: 48,
                      sm: 54,
                    },
                    height: {
                      xs: 48,
                      sm: 54,
                    },
                    bgcolor: "rgba(255,255,255,0.96)",
                    color: brandText,
                    border: "1px solid rgba(13,158,175,0.18)",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                    "&:hover": {
                      bgcolor: "#ffffff",
                    },
                  }}
                >
                  <CloseRoundedIcon
                    sx={{
                      fontSize: {
                        xs: 25,
                        sm: 28,
                      },
                    }}
                  />
                </IconButton>
              ) : null
            }
          />

          {/* text area */}
          <Box
            data-nosnippet
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.6,
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                letterSpacing: 0.4,
                color: brandText,
              }}
            >
              {text.title}
            </Typography>

            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 500,
                letterSpacing: 0.2,
                color: brandTeal,
              }}
            >
              {text.preparing}
            </Typography>

            {canCancel && (
              <Typography
                sx={{
                  fontSize: 12,
                  color: "rgba(78,90,97,0.78)",
                }}
              >
                {text.cancelHint}
              </Typography>
            )}
          </Box>

          {/* accent bar */}
          <Box
            sx={{
              mt: 0.4,
              width: "100%",
              height: 4,
              borderRadius: 999,
              overflow: "hidden",
              background: "rgba(13,158,175,0.08)",
            }}
          >
            <Box
              sx={{
                width: "45%",
                height: "100%",
                borderRadius: 999,
                background: `linear-gradient(90deg, ${brandTeal} 0%, ${brandBrown} 100%)`,
                animation: "sehoSpinCounter 1.8s ease-in-out infinite",
                transformOrigin: "left center",
              }}
            />
          </Box>
        </Box>
      </Backdrop>
    </LoadingContext.Provider>
  );
}
